import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TianlanStoreSwitcher from './TianlanStoreSwitcher';
import { callJamboProxy, getStoredShop, setStoredShop } from '../../utils/tianlanStore';
import usePageTitle from '../../hooks/usePageTitle';
import '../../styles.css';
import '../../tianlan-modern.css';

function TianlanJamboStoreManager() {
  usePageTitle('直播商店管理', '天藍小舖後台', '管理多商店登入憑證與切換');
  const [carts, setCarts] = useState([]);
  const [form, setForm] = useState({
    csrfToken: '',
    sessionId: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const loadCarts = async () => {
    setIsLoading(true);
    try {
      const result = await callJamboProxy('listCarts', {}, getStoredShop());
      setCarts(result.carts || []);
    } catch (error) {
      alert(`載入商店失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadCarts();
  }, []);

  const saveCart = async () => {
    if (!form.csrfToken || !form.sessionId) {
      alert('請輸入 csrfToken、sessionId');
      return;
    }
    setIsLoading(true);
    try {
      const result = await callJamboProxy('addCart', form, getStoredShop());
      const cart = result.cart || result;
      const shopUserId = cart.shopUserId || cart.shop_user_id || result.shop_user_id;
      const storeName = cart.storeName || cart.store_name || result.store_name || `商店 ${shopUserId}`;
      if (!shopUserId) throw new Error('系統沒有回傳商店 ID');
      setStoredShop({
        shopUserId,
        storeId: shopUserId,
        storeName,
      });
      setForm(prev => ({ ...prev, csrfToken: '', sessionId: '' }));
      await loadCarts();
      alert(`直播商店憑證已儲存：${storeName}（${shopUserId}）`);
    } catch (error) {
      alert(`儲存失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  const deleteCart = async (shopUserId) => {
    if (!window.confirm(`確定刪除商店 ${shopUserId} 的登入憑證嗎？`)) return;
    setIsLoading(true);
    try {
      await callJamboProxy('deleteCart', { shopUserId, shop_user_id: shopUserId }, getStoredShop());
      await loadCarts();
    } catch (error) {
      alert(`刪除失敗：${error.message}`);
    }
    setIsLoading(false);
  };

  return (
    <div className="modern-page">
      <div className="modern-page-header">
        <div className="modern-container">
          <div className="modern-page-title">
            <Link to="/exchange/tianlan" className="modern-back-btn">返回首頁</Link>
            <div>
              <h1>直播商店管理</h1>
              <div className="modern-page-subtitle">多店切換與登入憑證管理</div>
            </div>
          </div>
        </div>
      </div>

      <div className="modern-container tl-admin-workspace">
        <section className="tl-admin-hero">
          <div>
            <div className="tl-admin-kicker">Store credentials</div>
            <h2>多店登入狀態</h2>
            <p>貼上直播後台目前登入的 csrfToken 與 sessionId，系統會自動取得商店名稱與商店 ID，供商品規則、訂單查詢與客戶跨店查詢共用。</p>
          </div>
          <div className="tl-admin-meta">
            <span className="tl-pill">商店數 <strong>{carts.length}</strong></span>
            <span className="tl-pill">有效 <strong>{carts.filter(cart => !cart.authExpired).length}</strong></span>
          </div>
        </section>

        <TianlanStoreSwitcher />

        <div className="tl-panel tl-panel-pad">
          <div className="tl-panel-head">
            <div>
              <div className="tl-panel-title">從登入狀態新增商店</div>
              <div className="tl-panel-desc">不用手填商店 ID。只要貼登入憑證，系統會驗證並同步商店資料。</div>
            </div>
          </div>
          <div className="modern-grid modern-grid-2">
            <div className="modern-form-group">
              <label className="modern-form-label">csrfToken</label>
              <input className="modern-input" value={form.csrfToken} onChange={(e) => setForm({ ...form, csrfToken: e.target.value })} placeholder="csrftoken" />
            </div>
            <div className="modern-form-group">
              <label className="modern-form-label">sessionId</label>
              <input className="modern-input" value={form.sessionId} onChange={(e) => setForm({ ...form, sessionId: e.target.value })} placeholder="sessionid" />
            </div>
          </div>
          <button className="modern-btn modern-btn-primary" onClick={saveCart} disabled={isLoading}>
            {isLoading ? '串接中...' : '串接並切換到此商店'}
          </button>
        </div>

        <div className="tl-panel tl-panel-pad">
          <div className="tl-panel-head">
            <div>
              <div className="tl-panel-title">已設定商店</div>
              <div className="tl-panel-desc">這些商店會被客戶前台跨店查詢使用。</div>
            </div>
          </div>
          <div className="tl-list-stack">
            {carts.map(cart => (
              <div key={cart.id || cart.docId} className="tl-store-row">
                <div>
                  <div className="tl-row-title">{cart.storeName || cart.store_name || `商店 ${cart.shopUserId || cart.shop_user_id}`}</div>
                  <div className="tl-row-sub">商店 ID: {cart.shopUserId || cart.shop_user_id} / {cart.authExpired ? '憑證可能已過期' : '憑證有效'}</div>
                </div>
                <div className="tl-row-actions">
                  <button className="modern-btn modern-btn-secondary" onClick={() => setStoredShop({ shopUserId: cart.shopUserId || cart.shop_user_id, storeId: cart.shopUserId || cart.shop_user_id, storeName: cart.storeName || cart.store_name })}>切換</button>
                  <button className="modern-btn modern-btn-danger" onClick={() => deleteCart(cart.shopUserId || cart.shop_user_id)} disabled={isLoading}>刪除</button>
                </div>
              </div>
            ))}
            {carts.length === 0 && <div className="tl-empty-state">尚未設定直播商店。請先貼上 csrfToken / sessionId。</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TianlanJamboStoreManager;
